import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/feature/AdminLayout';
import { useAdminProfile } from '../../contexts/AdminProfileContext';
import {
  changeAdminPassword,
  deleteAdminAvatar,
  updateAdminProfile,
  uploadAdminAvatar,
} from '../../api/admin.api';

const inputCls =
  'w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition-all bg-white';

function Field({ label, desc, children }: { label: string; desc?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-1">{label}</label>
      {desc ? <p className="text-xs text-gray-400 mb-1.5">{desc}</p> : null}
      {children}
    </div>
  );
}

function formatDate(iso?: string | null) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ar-EG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function PasswordInput({
  value,
  onChange,
  show,
  onToggle,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  show: boolean;
  onToggle: () => void;
  placeholder?: string;
}) {
  return (
    <div className="relative">
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`${inputCls} pe-10`}
        autoComplete="new-password"
      />
      <button
        type="button"
        onClick={onToggle}
        className="absolute end-2.5 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
      >
        <i className={show ? 'ri-eye-off-line' : 'ri-eye-line'} />
      </button>
    </div>
  );
}

export default function ProfilePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { admin, loading, refresh } = useAdminProfile();
  const fileRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPw, setShowPw] = useState({ current: false, next: false, confirm: false });

  const [profileMsg, setProfileMsg] = useState('');
  const [profileErr, setProfileErr] = useState('');
  const [passMsg, setPassMsg] = useState('');
  const [passErr, setPassErr] = useState('');
  const [avatarMsg, setAvatarMsg] = useState('');
  const [avatarErr, setAvatarErr] = useState('');

  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPass, setSavingPass] = useState(false);
  const [savingAvatar, setSavingAvatar] = useState(false);

  useEffect(() => {
    setName(admin?.name ?? '');
    setEmail(admin?.email ?? '');
    setPhone(admin?.phone ?? '');
  }, [admin]);

  const initials = (admin?.name ?? 'SA')
    .split(/\s+/)
    .map((p) => p[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const dirty =
    name.trim() !== (admin?.name ?? '') ||
    email.trim() !== (admin?.email ?? '') ||
    phone.trim() !== (admin?.phone ?? '');

  const handleSaveProfile = async () => {
    setProfileMsg('');
    setProfileErr('');
    if (!name.trim() || !email.trim()) {
      setProfileErr(t('profile.validation_required'));
      return;
    }
    setSavingProfile(true);
    const r = await updateAdminProfile({
      name: name.trim(),
      email: email.trim(),
      phone: phone.trim() || null,
    });
    setSavingProfile(false);
    if (r.ok === false) {
      if (r.unauthorized) {
        navigate('/admin/login', { replace: true });
        return;
      }
      setProfileErr(r.message || t('profile.save_failed'));
      return;
    }
    await refresh();
    setProfileMsg(t('profile.saved'));
  };

  const handleChangePassword = async () => {
    setPassMsg('');
    setPassErr('');
    if (!currentPassword || !newPassword) {
      setPassErr(t('profile.password_required'));
      return;
    }
    if (newPassword.length < 8) {
      setPassErr(t('profile.password_min'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setPassErr(t('profile.password_mismatch'));
      return;
    }
    setSavingPass(true);
    const r = await changeAdminPassword({
      current_password: currentPassword,
      new_password: newPassword,
      new_password_confirmation: confirmPassword,
    });
    setSavingPass(false);
    if (r.ok === false) {
      if (r.unauthorized) {
        navigate('/admin/login', { replace: true });
        return;
      }
      setPassErr(r.message || t('profile.password_failed'));
      return;
    }
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPassMsg(t('profile.password_changed'));
  };

  const handleAvatarPick = async (file?: File | null) => {
    setAvatarMsg('');
    setAvatarErr('');
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setAvatarErr(t('profile.avatar_invalid'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setAvatarErr(t('profile.avatar_too_large'));
      return;
    }
    setSavingAvatar(true);
    const r = await uploadAdminAvatar(file);
    setSavingAvatar(false);
    if (r.ok === false) {
      if (r.unauthorized) {
        navigate('/admin/login', { replace: true });
        return;
      }
      setAvatarErr(r.message || t('profile.avatar_failed'));
      return;
    }
    await refresh();
    setAvatarMsg(t('profile.avatar_saved'));
  };

  const handleDeleteAvatar = async () => {
    setAvatarMsg('');
    setAvatarErr('');
    setSavingAvatar(true);
    const r = await deleteAdminAvatar();
    setSavingAvatar(false);
    if (r.ok === false) {
      if (r.unauthorized) {
        navigate('/admin/login', { replace: true });
        return;
      }
      setAvatarErr(r.message || t('profile.avatar_failed'));
      return;
    }
    await refresh();
    setAvatarMsg(t('profile.avatar_removed'));
  };

  const statusLabel =
    admin?.status === 'active'
      ? t('profile.status_active')
      : admin?.status === 'suspended'
        ? t('profile.status_suspended')
        : admin?.status ?? '—';

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto flex flex-col gap-5">
        {/* Hero card */}
        <div className="bg-white rounded-2xl ring-1 ring-gray-100 p-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-5">
            <div className="relative group">
              {admin?.avatar_url ? (
                <img
                  src={admin.avatar_url}
                  alt={admin.name}
                  className="w-20 h-20 rounded-2xl object-cover ring-2 ring-teal-100"
                />
              ) : (
                <div className="w-20 h-20 rounded-2xl bg-teal-600 text-white flex items-center justify-center text-2xl font-black">
                  {loading && !admin ? '…' : initials}
                </div>
              )}
              <button
                type="button"
                disabled={savingAvatar}
                onClick={() => fileRef.current?.click()}
                className="absolute -bottom-1 -end-1 w-8 h-8 rounded-full bg-gray-900 text-white flex items-center justify-center shadow-md hover:bg-teal-600 transition-colors cursor-pointer disabled:opacity-60"
                title={t('profile.change_avatar')}
              >
                <i className={`text-sm ${savingAvatar ? 'ri-loader-4-line animate-spin' : 'ri-camera-line'}`} />
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/jpg"
                className="hidden"
                onChange={(e) => void handleAvatarPick(e.target.files?.[0])}
              />
            </div>

            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-black text-gray-900 truncate">{admin?.name ?? '—'}</h2>
              <p className="text-sm text-gray-500 mt-0.5 truncate" dir="ltr">
                {admin?.email ?? '—'}
              </p>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <span className="inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full bg-teal-50 text-teal-700">
                  <i className="ri-shield-user-line" />
                  {admin?.role?.name ?? t('admin.role')}
                </span>
                <span
                  className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full ${
                    admin?.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                  }`}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-current" />
                  {statusLabel}
                </span>
              </div>
            </div>

            {admin?.avatar_url ? (
              <button
                type="button"
                disabled={savingAvatar}
                onClick={() => void handleDeleteAvatar()}
                className="self-start px-3 py-2 rounded-xl text-xs font-bold text-rose-600 bg-rose-50 hover:bg-rose-100 disabled:opacity-60"
              >
                {t('profile.remove_avatar')}
              </button>
            ) : null}
          </div>

          {(avatarErr || avatarMsg) && (
            <div
              className={`mt-4 rounded-xl px-3 py-2.5 text-sm font-medium ${
                avatarErr ? 'bg-rose-50 text-rose-700' : 'bg-emerald-50 text-emerald-700'
              }`}
            >
              {avatarErr || avatarMsg}
            </div>
          )}

          <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-xl bg-gray-50 px-4 py-3">
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide">{t('profile.phone')}</p>
              <p className="text-sm font-bold text-gray-900 mt-1" dir="ltr">
                {admin?.phone || '—'}
              </p>
            </div>
            <div className="rounded-xl bg-gray-50 px-4 py-3">
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide">{t('profile.last_login')}</p>
              <p className="text-sm font-bold text-gray-900 mt-1">{formatDate(admin?.last_login_at)}</p>
            </div>
            <div className="rounded-xl bg-gray-50 px-4 py-3">
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide">{t('profile.joined')}</p>
              <p className="text-sm font-bold text-gray-900 mt-1">{formatDate(admin?.created_at)}</p>
            </div>
          </div>
        </div>

        {/* Basic info */}
        <div className="bg-white rounded-2xl ring-1 ring-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center">
              <i className="ri-user-settings-line text-lg" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900">{t('profile.info_title')}</h3>
              <p className="text-xs text-gray-400">{t('profile.info_subtitle')}</p>
            </div>
          </div>
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label={t('profile.name')}>
                <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
              </Field>
              <Field label={t('profile.email')}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputCls}
                  dir="ltr"
                />
              </Field>
              <Field label={t('profile.phone')} desc={t('profile.phone_hint')}>
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className={inputCls}
                  placeholder="+966 5X XXX XXXX"
                  dir="ltr"
                />
              </Field>
            </div>

            {profileErr ? (
              <div className="rounded-xl bg-rose-50 text-rose-700 text-sm px-3 py-2.5 font-medium">{profileErr}</div>
            ) : null}
            {profileMsg ? (
              <div className="rounded-xl bg-emerald-50 text-emerald-700 text-sm px-3 py-2.5 font-medium flex items-center gap-1.5">
                <i className="ri-checkbox-circle-fill" />
                {profileMsg}
              </div>
            ) : null}

            <div className="flex justify-end pt-2 border-t border-gray-100">
              <button
                type="button"
                disabled={savingProfile || !dirty}
                onClick={() => void handleSaveProfile()}
                className="px-5 py-2.5 rounded-xl bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700 disabled:opacity-50 cursor-pointer inline-flex items-center gap-2"
              >
                {savingProfile ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-save-line" />}
                {t('profile.save')}
              </button>
            </div>
          </div>
        </div>

        {/* Password */}
        <div id="password" className="bg-white rounded-2xl ring-1 ring-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
              <i className="ri-lock-password-line text-lg" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900">{t('profile.password_title')}</h3>
              <p className="text-xs text-gray-400">{t('profile.password_subtitle')}</p>
            </div>
          </div>
          <div className="p-6 space-y-4">
            <Field label={t('profile.current_password')}>
              <PasswordInput
                value={currentPassword}
                onChange={setCurrentPassword}
                show={showPw.current}
                onToggle={() => setShowPw((s) => ({ ...s, current: !s.current }))}
              />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label={t('profile.new_password')} desc={t('profile.password_min')}>
                <PasswordInput
                  value={newPassword}
                  onChange={setNewPassword}
                  show={showPw.next}
                  onToggle={() => setShowPw((s) => ({ ...s, next: !s.next }))}
                />
              </Field>
              <Field label={t('profile.confirm_password')}>
                <PasswordInput
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  show={showPw.confirm}
                  onToggle={() => setShowPw((s) => ({ ...s, confirm: !s.confirm }))}
                />
              </Field>
            </div>

            {passErr ? (
              <div className="rounded-xl bg-rose-50 text-rose-700 text-sm px-3 py-2.5 font-medium">{passErr}</div>
            ) : null}
            {passMsg ? (
              <div className="rounded-xl bg-emerald-50 text-emerald-700 text-sm px-3 py-2.5 font-medium flex items-center gap-1.5">
                <i className="ri-checkbox-circle-fill" />
                {passMsg}
              </div>
            ) : null}

            <div className="flex justify-end pt-2 border-t border-gray-100">
              <button
                type="button"
                disabled={savingPass}
                onClick={() => void handleChangePassword()}
                className="px-5 py-2.5 rounded-xl border border-teal-600 text-teal-700 text-sm font-semibold hover:bg-teal-50 disabled:opacity-60 cursor-pointer inline-flex items-center gap-2"
              >
                {savingPass ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-shield-keyhole-line" />}
                {t('profile.change_password')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
